import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Bell, ChevronRight } from "lucide-react";

import {
    usePayments,
    useSendPaymentReminder,
} from "../../application/usePayments";
import { Payment, canSendReminder, formatMoney } from "../../domain/types";

/** How many to list before pointing at the full list. */
const VISIBLE = 5;

/**
 * What is still owed, on the therapist's home page.
 *
 * Shows only `pending` — a draft has been sent to nobody and a paid one needs
 * no attention, so neither belongs on a page meant to answer "what needs me
 * today". Renders nothing when nothing is outstanding rather than an empty
 * card congratulating the therapist.
 */
export default function PendingPaymentsCard() {
    const { t } = useTranslation();
    const { data, isLoading, isError } = usePayments({
        status: "pending",
        limit: VISIBLE,
    });
    const sendReminder = useSendPaymentReminder();

    // Silent on failure: this is one card among several, and a therapist whose
    // home page shouts about a background request cannot tell what is broken.
    if (isLoading || isError || !data || data.total === 0) return null;

    return (
        <div className="rounded-xl border border-border-subtle bg-surface-raised p-5 sm:p-6">
            <div className="mb-3 flex items-center justify-between gap-3">
                {/* Same heading as the next-session card beside it. */}
                <h3 className="text-sm font-semibold text-content-primary">
                    {t("payments.home.title")}
                </h3>
                <Link
                    to="/payments?status=pending"
                    className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:underline"
                >
                    {t("payments.home.seeAll", { count: data.total })}
                    <ChevronRight className="h-4 w-4" />
                </Link>
            </div>

            <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                {data.items.map((payment) => (
                    <li
                        key={payment.id}
                        className="flex items-center justify-between gap-3 py-2.5"
                    >
                        <Link to={`/payments/${payment.id}`} className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-content-primary">
                                {payment.patient_name ?? "—"}
                            </span>
                            <span className="block truncate text-xs text-content-tertiary">
                                {payment.due_date
                                    ? t("payments.home.dueOn", { date: payment.due_date })
                                    : (payment.line_items[0]?.description ?? "")}
                            </span>
                        </Link>
                        <span className="shrink-0 text-sm font-semibold text-content-primary">
                            {formatMoney(payment.total)}
                        </span>
                        <ChaseButton payment={payment} onChase={sendReminder.mutate} />
                    </li>
                ))}
            </ul>
        </div>
    );
}

/**
 * Chase without leaving the home page.
 *
 * Hidden unless the server says another reminder may go — it owns the cap and
 * the once-a-day rule, and a button that decides for itself is a button that
 * can disagree with the endpoint behind it.
 */
const ChaseButton: React.FC<{
    payment: Payment;
    onChase: (id: string) => void;
}> = ({ payment, onChase }) => {
    const { t } = useTranslation();

    if (!canSendReminder(payment)) return null;

    return (
        <button
            type="button"
            onClick={() => onChase(payment.id)}
            aria-label={t("payments.actions.sendReminder")}
            title={t("payments.actions.sendReminder")}
            className="shrink-0 rounded-lg p-1.5 text-content-tertiary transition-colors hover:bg-gray-100 hover:text-brand-600 dark:hover:bg-white/[0.06]"
        >
            <Bell className="h-4 w-4" />
        </button>
    );
};
